import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../../../auth/firebase';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Line, Bar, Pie } from 'react-chartjs-2';
import GeoGrowth from './growth/geo_growth';
import './growth_analytics.css';

// # Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

// # Chart options
const pieChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: {
          size: 14
        }
      }
    },
    title: {
      display: true,
      text: 'User Distribution by Academic Phase',
      font: {
        size: 16
      }
    }
  }
};

// # Chart options for overview pie charts
const overviewPieOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'right',
      labels: {
        font: { size: 14 }
      }
    }
  }
};

const GrowthAnalytics = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [countryFilter, setCountryFilter] = useState('');
  const [countrySortConfig, setCountrySortConfig] = useState({
    key: 'count',
    direction: 'desc'
  });
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    deletedUsers: 0,
    disabledUsers: 0,
    verifiedUsers: 0,
    usersByCountry: {},
    usersByPhase: {},
    genderDemographics: {
      male: 0,
      female: 0
    }
  });
  const [isLoading, setIsLoading] = useState(true);
  const [growthData, setGrowthData] = useState({
    daily: [],
    weekly: [],
    monthly: []
  });

  // # Add refs for tracking last fetch times
  const lastAnalyticsFetchRef = React.useRef({
    timestamp: 0,
    logged: false
  });
  
  const lastGrowthFetchRef = React.useRef({
    timestamp: 0,
    logged: false
  });

  // # Fetch analytics and growth data together
  useEffect(() => {
    let isMounted = true;
    
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const startTime = performance.now();
        
        // # Single Firestore read for users collection
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const usersData = usersSnapshot.docs.map(doc => {
          const data = doc.data();
          // # Ensure we get createdAt from metadata or fallback to current time
          return {
            ...data,
            createdAt: data.metadata?.createdAt || data.created_at || new Date().toISOString()
          };
        });
        
        // # Get deleted users count from system_logs instead of admin_activity_logs
        let deletedUsersCount = 0;
        
        // # Get current year and month for the system_logs document ID
        const currentDate = new Date();
        const currentYearMonth = `${currentDate.getFullYear()}_${(currentDate.getMonth() + 1).toString().padStart(2, '0')}`;
        
        // # Check the last few months of system logs for deletion records
        // # This ensures we capture deletion history spanning multiple months
        const yearMonthsToCheck = [];
        
        // # Add current month and previous 5 months to check
        for (let i = 0; i < 6; i++) {
          const checkDate = new Date();
          checkDate.setMonth(currentDate.getMonth() - i);
          const yearMonth = `${checkDate.getFullYear()}_${(checkDate.getMonth() + 1).toString().padStart(2, '0')}`;
          yearMonthsToCheck.push(yearMonth);
        }
        
        // # Check each month's logs for deletions
        for (const yearMonth of yearMonthsToCheck) {
          const systemLogRef = doc(db, 'system_logs', yearMonth);
          const systemLogDoc = await getDoc(systemLogRef);
          
          if (systemLogDoc.exists()) {
            const logData = systemLogDoc.data();
            
            // # Count admin user deletions
            if (logData.user_deletions) {
              deletedUsersCount += Object.keys(logData.user_deletions).length;
            }
            
            // # Count self-deletions
            if (logData.user_self_deletions) {
              deletedUsersCount += Object.keys(logData.user_self_deletions).length;
            }
          }
        }
        
        if (!isMounted) return;
        
        const currentTimestamp = Date.now();
        // # Log analytics fetch
        if (!(currentTimestamp - lastAnalyticsFetchRef.current.timestamp < 5000 && lastAnalyticsFetchRef.current.logged)) {
          console.log('Growth Analytics - Fetching analytics data:', {
            operation: 'fetch_analytics',
            usersCount: usersData.length,
            deletedUsersCount: deletedUsersCount,
            timestamp: new Date().toISOString(),
            timeElapsed: `${(performance.now() - startTime).toFixed(2)}ms`
          });
          lastAnalyticsFetchRef.current = { timestamp: currentTimestamp, logged: true };
        }
        
        // # Process analytics data
        const processedData = usersData.reduce((acc, user) => {
          const country = user.nationality || 'Unknown';
          acc.usersByCountry[country] = (acc.usersByCountry[country] || 0) + 1;
          
          const phase = user.phase || 'Unknown';
          acc.usersByPhase[phase] = (acc.usersByPhase[phase] || 0) + 1;
          
          if (user.gender === 'male') acc.genderDemographics.male++;
          if (user.gender === 'female') acc.genderDemographics.female++;
          
          if (user.emailVerified) acc.verifiedUsers++;
          
          // # Count user statuses
          if (user.bans?.isBanned) acc.bannedUsers++;
          else if (user.suspends?.isSuspended) acc.suspendedUsers++;
          else if (user.status === 'active' || !user.status) acc.activeUsers++;
          
          return acc;
        }, {
          usersByCountry: {},
          usersByPhase: {},
          genderDemographics: { male: 0, female: 0 },
          verifiedUsers: 0,
          activeUsers: 0,
          bannedUsers: 0,
          suspendedUsers: 0
        });

        setAnalyticsData({
          ...processedData,
          totalUsers: usersData.length,
          deletedUsers: deletedUsersCount
        });

        // # Log growth fetch
        if (!(currentTimestamp - lastGrowthFetchRef.current.timestamp < 5000 && lastGrowthFetchRef.current.logged)) {
          console.log('Growth Analytics - Processing growth data:', {
            operation: 'process_growth',
            totalUsers: usersData.length,
            usersWithCreatedAt: usersData.filter(user => user.createdAt).length,
            timestamp: new Date().toISOString()
          });
          lastGrowthFetchRef.current = { timestamp: currentTimestamp, logged: true };
        }

        // # Process growth data - ensure all users are counted
        const sortedUsers = usersData
          .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

        // # Generate daily data for last 30 days
        const daily = Array.from({ length: 30 }, (_, i) => {
          const date = new Date();
          date.setDate(date.getDate() - (29 - i));
          date.setHours(0, 0, 0, 0); // # Set to start of day
          
          const nextDate = new Date(date);
          nextDate.setDate(nextDate.getDate() + 1);
          
          const count = sortedUsers.filter(user => {
            const userDate = new Date(user.createdAt);
            return userDate >= date && userDate < nextDate;
          }).length;
          
          return {
            date: date.toISOString().split('T')[0],
            count
          };
        });

        // # Generate weekly data for last 12 weeks
        const weekly = Array.from({ length: 12 }, (_, i) => {
          const endDate = new Date();
          endDate.setDate(endDate.getDate() - (i * 7));
          endDate.setHours(23, 59, 59, 999); // # Set to end of day
          
          const startDate = new Date(endDate);
          startDate.setDate(startDate.getDate() - 7);
          startDate.setHours(0, 0, 0, 0); // # Set to start of day
          
          const count = sortedUsers.filter(user => {
            const userDate = new Date(user.createdAt);
            return userDate >= startDate && userDate <= endDate;
          }).length;

          return {
            week: `Week ${12 - i}`,
            count
          };
        }).reverse();

        // # Generate monthly data for last 12 months
        const monthly = Array.from({ length: 12 }, (_, i) => {
          const date = new Date();
          date.setMonth(date.getMonth() - (11 - i));
          date.setDate(1);
          date.setHours(0, 0, 0, 0); // # Set to start of month
          
          const endDate = new Date(date);
          endDate.setMonth(endDate.getMonth() + 1);
          endDate.setDate(0);
          endDate.setHours(23, 59, 59, 999); // # Set to end of month
          
          const count = sortedUsers.filter(user => {
            const userDate = new Date(user.createdAt);
            return userDate >= date && userDate <= endDate;
          }).length;

          return {
            month: `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`,
            count
          };
        });

        setGrowthData({ daily, weekly, monthly });
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, []); // Single effect for both analytics and growth data

  // # Sort countries by selected column
  const getSortedCountries = () => {
    const countries = Object.entries(analyticsData.usersByCountry)
      .map(([country, count]) => ({
        country,
        count,
        percentage: ((count / analyticsData.totalUsers) * 100).toFixed(1)
      }))
      .filter(item => 
        item.country.toLowerCase().includes(countryFilter.toLowerCase())
      );

    return countries.sort((a, b) => {
      if (countrySortConfig.key === 'country') {
        return countrySortConfig.direction === 'asc' 
          ? a.country.localeCompare(b.country)
          : b.country.localeCompare(a.country);
      } else {
        const aValue = countrySortConfig.key === 'count' ? a.count : parseFloat(a.percentage);
        const bValue = countrySortConfig.key === 'count' ? b.count : parseFloat(b.percentage);
        return countrySortConfig.direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
    });
  };

  // # Handle column sort
  const handleSort = (key) => {
    setCountrySortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // # Get sort direction indicator
  const getSortIndicator = (columnKey) => {
    if (countrySortConfig.key !== columnKey) return '↕';
    return countrySortConfig.direction === 'asc' ? '↑' : '↓';
  };

  // # Prepare data for phase distribution chart
  const getPhaseChartData = () => {
    const phases = {
      'basic-science': 'Academic Phase',
      'clinical-medicine': 'Clinical Phase',
      'intern': 'Intern',
      'new-graduate': 'New Graduate',
      'old-graduate': 'Old Graduate'
    };

    const data = {
      labels: [],
      datasets: [{
        data: [],
        backgroundColor: [
          'pink',
          '#3498db',
          '#e74c3c',
          '#2ecc71',
          '#f1c40f'
        ],
        borderWidth: 1
      }]
    };

    Object.entries(analyticsData.usersByPhase).forEach(([phase, count]) => {
      data.labels.push(phases[phase] || phase);
      data.datasets[0].data.push(count);
    });

    return data;
  };

  // # Prepare data for gender distribution chart
  const getGenderChartData = () => {
    const { male, female } = analyticsData.genderDemographics;
    const total = male + female;
    
    return {
      labels: ['Male', 'Female'],
      datasets: [{
        data: [male, female],
        backgroundColor: ['#3498db', '#e74c3c'],
        borderWidth: 1
      }]
    };
  };

  // # Gender chart options
  const genderChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          font: {
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Gender Distribution',
        font: {
          size: 16
        }
      }
    }
  };

  // # Line chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      }
    }
  };

  // # Prepare data for user status pie chart
  const getUserStatusData = () => {
    const active = analyticsData.activeUsers;
    const banned = analyticsData.bannedUsers;
    const suspended = analyticsData.suspendedUsers;

    return {
      labels: ['Active', 'Banned', 'Suspended'],
      datasets: [{
        data: [active, banned, suspended],
        backgroundColor: [
          '#2ecc71', // Active - Green
          '#e74c3c', // Banned - Red
          '#f1c40f'  // Suspended - Yellow
        ],
        borderWidth: 1
      }]
    };
  };

  // # Prepare data for verification status pie chart
  const getVerificationData = () => {
    const verified = analyticsData.verifiedUsers;
    const unverified = analyticsData.totalUsers - verified;

    return {
      labels: ['Verified', 'Unverified'],
      datasets: [{
        data: [verified, unverified],
        backgroundColor: [
          '#3498db', // Verified - Blue
          '#e67e22'  // Unverified - Orange
        ],
        borderWidth: 1
      }]
    };
  };

  return (
    <div className="grth_ltc_admin-page">
      <SharedHeader />
      
      <main className="grth_ltc_admin-content">
        <div className="grth_ltc_admin-header">
          <button 
            className="grth_ltc_back-button"
            onClick={() => navigate('/admin/analytics')}
          >
            <i className="fas fa-arrow-left"></i> Back to Analytics
          </button>
          <h1>Growth Analytics</h1>
        </div>

        {isLoading ? (
          <div className="grth_ltc_loading">Loading analytics data...</div>
        ) : (
          <Tabs
            selectedIndex={activeTab}
            onSelect={index => setActiveTab(index)}
            className="grth_ltc_tabs"
          >
            <TabList>
              <Tab>Overview</Tab>
              <Tab>Geographic Distribution</Tab>
              <Tab>Academic Demographics</Tab>
              <Tab>Gender Distribution</Tab>
              <Tab>Growth Trends</Tab>
            </TabList>

            <TabPanel>
              <div className="grth_ltc_overview-panel">
                <div className="grth_ltc_stats-grid">
                  <div className="grth_ltc_stat-card">
                    <h3>Total Users</h3>
                    <p>{analyticsData.totalUsers}</p>
                  </div>
                  <div className="grth_ltc_stat-card">
                    <h3>Active Users</h3>
                    <p>{analyticsData.activeUsers}</p>
                  </div>
                  <div className="grth_ltc_stat-card">
                    <h3>Deleted Users</h3>
                    <p>{analyticsData.deletedUsers}</p>
                  </div>
                  <div className="grth_ltc_stat-card">
                    <h3>Disabled Users</h3>
                    <p>{analyticsData.disabledUsers}</p>
                  </div>
                  <div className="grth_ltc_stat-card">
                    <h3>Verified Users</h3>
                    <p>{analyticsData.verifiedUsers}</p>
                  </div>
                </div>

                <div className="grth_ltc_overview-charts-grid">
                  <div className="grth_ltc_chart-container">
                    <h3>User Status Distribution</h3>
                    <div className="grth_ltc_overview-pie-chart">
                      <Pie 
                        data={getUserStatusData()} 
                        options={{
                          ...overviewPieOptions,
                          plugins: {
                            ...overviewPieOptions.plugins,
                            title: {
                              display: true,
                              text: 'User Status Distribution',
                              font: { size: 16, weight: 'bold' }
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.raw;
                                  const percentage = ((value / analyticsData.totalUsers) * 100).toFixed(1);
                                  return `${context.label}: ${value} users (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>

                  <div className="grth_ltc_chart-container">
                    <h3>Verification Status</h3>
                    <div className="grth_ltc_overview-pie-chart">
                      <Pie 
                        data={getVerificationData()} 
                        options={{
                          ...overviewPieOptions,
                          plugins: {
                            ...overviewPieOptions.plugins,
                            title: {
                              display: true,
                              text: 'User Verification Status',
                              font: { size: 16, weight: 'bold' }
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.raw;
                                  const percentage = ((value / analyticsData.totalUsers) * 100).toFixed(1);
                                  return `${context.label}: ${value} users (${percentage}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel>
              <GeoGrowth analyticsData={analyticsData} />
            </TabPanel>

            <TabPanel>
              <div className="grth_ltc_academic-panel">
                <h2>Academic Demographics</h2>
                
                <div className="grth_ltc_chart-container">
                  <div className="grth_ltc_pie-chart">
                    <Pie data={getPhaseChartData()} options={pieChartOptions} />
                  </div>
                </div>

                <div className="grth_ltc_chart-container">
                  <h3>Phase Distribution Details</h3>
                  <div className="grth_ltc_phase-stats">
                    <table className="grth_ltc_stats-table">
                      <thead>
                        <tr>
                          <th>Academic Phase</th>
                          <th>Users</th>
                          <th>Percentage</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(analyticsData.usersByPhase).map(([phase, count]) => {
                          const percentage = ((count / analyticsData.totalUsers) * 100).toFixed(1);
                          const displayPhase = {
                            'basic-science': 'Academic Phase',
                            'clinical-medicine': 'Clinical Phase',
                            'intern': 'Intern',
                            'new-graduate': 'New Graduate',
                            'old-graduate': 'Old Graduate'
                          }[phase] || phase;

                          return (
                            <tr key={phase}>
                              <td>{displayPhase}</td>
                              <td>{count}</td>
                              <td>{percentage}%</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel>
              <div className="grth_ltc_gender-panel">
                <h2>Gender Distribution</h2>
                
                <div className="grth_ltc_stats-grid">
                  <div className="grth_ltc_stat-card">
                    <h3>Male Users</h3>
                    <p>{analyticsData.genderDemographics.male}</p>
                    <span className="grth_ltc_percentage">
                      {((analyticsData.genderDemographics.male / analyticsData.totalUsers) * 100).toFixed(1)}%
                    </span>
                  </div>
                  <div className="grth_ltc_stat-card">
                    <h3>Female Users</h3>
                    <p>{analyticsData.genderDemographics.female}</p>
                    <span className="grth_ltc_percentage">
                      {((analyticsData.genderDemographics.female / analyticsData.totalUsers) * 100).toFixed(1)}%
                    </span>
                  </div>
                </div>

                <div className="grth_ltc_chart-container">
                  <div className="grth_ltc_pie-chart">
                    <Pie data={getGenderChartData()} options={genderChartOptions} />
                  </div>
                </div>
              </div>
            </TabPanel>

            <TabPanel>
              <div className="grth_ltc_trends-panel">
                <h2>Growth Trends</h2>

                <div className="grth_ltc_chart-container">
                  <h3>Daily New Users (Last 30 Days)</h3>
                  <div className="grth_ltc_line-chart">
                    <Line
                      data={{
                        labels: growthData.daily.map(d => d.date),
                        datasets: [{
                          label: 'New Users',
                          data: growthData.daily.map(d => d.count),
                          borderColor: '#2c3e50',
                          tension: 0.4
                        }]
                      }}
                      options={lineChartOptions}
                    />
                  </div>
                </div>

                <div className="grth_ltc_chart-container">
                  <h3>Weekly New Users (Last 12 Weeks)</h3>
                  <div className="grth_ltc_line-chart">
                    <Line
                      data={{
                        labels: growthData.weekly.map(w => w.week),
                        datasets: [{
                          label: 'New Users',
                          data: growthData.weekly.map(w => w.count),
                          borderColor: '#3498db',
                          tension: 0.4
                        }]
                      }}
                      options={lineChartOptions}
                    />
                  </div>
                </div>

                <div className="grth_ltc_chart-container">
                  <h3>Monthly New Users (Last 12 Months)</h3>
                  <div className="grth_ltc_line-chart">
                    <Line
                      data={{
                        labels: growthData.monthly.map(m => m.month),
                        datasets: [{
                          label: 'New Users',
                          data: growthData.monthly.map(m => m.count),
                          borderColor: '#e74c3c',
                          tension: 0.4
                        }]
                      }}
                      options={lineChartOptions}
                    />
                  </div>
                </div>
              </div>
            </TabPanel>
          </Tabs>
        )}
      </main>

      <DashboardFooter />
    </div>
  );
};

export default GrowthAnalytics;
